(() => {
  'use strict';

  const moneyFormatter = new Intl.NumberFormat('en-NZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const mediaArrowContrastThreshold = 0.55;

  function parseVariants(wrapper) {
    const node = wrapper.querySelector('[data-cosmoshop-installment-variants]');
    if (!node) return [];
    try {
      const variants = JSON.parse(node.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      return [];
    }
  }

  function productScope(element) {
    return element.closest('.shopify-section, .product-section, section') || document;
  }

  function selectedVariantId(wrapper) {
    const scope = productScope(wrapper);
    const formInput = scope.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (formInput && formInput.value) return String(formInput.value);

    const params = new URLSearchParams(window.location.search || '');
    return params.get('variant') ? String(params.get('variant')) : null;
  }

  function selectedQuantity(wrapper) {
    const scope = productScope(wrapper);
    const input = scope.querySelector('form[action*="/cart/add"] input[name="quantity"], input[name="quantity"][form^="product-form-"]');
    const amount = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(amount) && amount > 0 ? amount : 1;
  }

  function cartUrl(routeName, fallback) {
    const url = (window.routes && window.routes[routeName]) || fallback;
    return url.includes('.js') ? url : url.replace(/(\?.*)?$/, (query) => `.js${query || ''}`);
  }

  function checkoutAddUrl(variantId, quantity) {
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return `${addUrl}?id=${encodeURIComponent(variantId)}&quantity=${encodeURIComponent(quantity || 1)}&return_to=%2Fcheckout`;
  }

  function setPaymentOptionVisibility(scope, available) {
    if (!scope || !scope.querySelectorAll) return;

    scope.querySelectorAll('[data-cosmoshop-payment-option-group]').forEach((group) => {
      group.hidden = !available;
      if (available) group.removeAttribute('aria-hidden');
      else group.setAttribute('aria-hidden', 'true');
    });
  }

  function updateInstallmentWrapper(wrapper) {
    const variants = parseVariants(wrapper);
    if (!variants.length) return;

    const currentId = selectedVariantId(wrapper);
    const selected = variants.find((variant) => String(variant.id) === String(currentId)) || variants[0];
    const quantity = selectedQuantity(wrapper);
    const available = selected.available !== false;
    const amount = `$${moneyFormatter.format(Number(selected.price || 0) / 400)}`;

    setPaymentOptionVisibility(productScope(wrapper), available);

    wrapper.querySelectorAll('[data-cosmoshop-installment-amount]').forEach((node) => {
      node.textContent = amount;
    });

    wrapper.querySelectorAll('[data-cosmoshop-installment-checkout]').forEach((link) => {
      if (available) {
        link.href = checkoutAddUrl(selected.id, quantity);
        link.classList.remove('is-disabled');
        link.removeAttribute('aria-disabled');
      } else {
        link.href = '#';
        link.classList.add('is-disabled');
        link.setAttribute('aria-disabled', 'true');
      }
    });
  }

  function updateAllInstallments() {
    document.querySelectorAll('[data-cosmoshop-installments]').forEach(updateInstallmentWrapper);
  }

  function redirectToCheckout(provider) {
    const checkout = provider ? `/checkout?preferred_payment=${encodeURIComponent(provider)}` : '/checkout';
    window.location.href = checkout;
  }

  async function beginInstallmentCheckout(link) {
    if (!link || link.getAttribute('aria-disabled') === 'true') return;

    const wrapper = link.closest('[data-cosmoshop-installments]');
    if (!wrapper) return;

    const variantId = selectedVariantId(wrapper);
    if (!variantId) return;

    const quantity = selectedQuantity(wrapper);
    const provider = link.getAttribute('data-cosmoshop-payment-provider') || '';
    const providerLabel = link.getAttribute('data-cosmoshop-payment-label') || provider || 'Installment payment';
    const fallbackUrl = checkoutAddUrl(variantId, quantity);

    link.classList.add('is-loading');
    link.setAttribute('aria-busy', 'true');

    if (!window.fetch) {
      window.location.href = fallbackUrl;
      return;
    }

    try {
      const addResponse = await fetch(cartUrl('cart_add_url', '/cart/add'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity,
            properties: { '_Preferred payment': providerLabel }
          }]
        })
      });

      if (!addResponse.ok) throw new Error('Cart add failed');

      await fetch(cartUrl('cart_update_url', '/cart/update'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ attributes: { 'Preferred payment': providerLabel } })
      }).catch(() => null);

      redirectToCheckout(provider);
    } catch (error) {
      window.location.href = fallbackUrl;
    }
  }

  function setMediaPaginationActive(pagination, activeIndex) {
    if (!pagination) return;
    pagination.querySelectorAll('[data-cosmoshop-media-dot]').forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function updatePaginationFromScroll(pagination) {
    const slider = pagination.closest('slider-component');
    const mediaList = slider && slider.querySelector('.product__media-list');
    if (!mediaList) return;

    const slides = Array.from(mediaList.querySelectorAll('.product__media-item'));
    if (!slides.length) return;

    let activeIndex = 0;
    let smallestDistance = Infinity;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - mediaList.scrollLeft);
      if (distance < smallestDistance) {
        activeIndex = index;
        smallestDistance = distance;
      }
    });

    setMediaPaginationActive(pagination, activeIndex);

    const prevArrow = slider.querySelector('.cosmoshop-media-arrow--prev');
    const nextArrow = slider.querySelector('.cosmoshop-media-arrow--next');
    const atFirstSlide = activeIndex <= 0;
    const atLastSlide = activeIndex >= slides.length - 1;
    if (prevArrow) {
      prevArrow.hidden = atFirstSlide;
      if (atFirstSlide) prevArrow.setAttribute('disabled', 'disabled');
      else prevArrow.removeAttribute('disabled');
    }
    if (nextArrow) {
      nextArrow.hidden = atLastSlide;
      if (atLastSlide) nextArrow.setAttribute('disabled', 'disabled');
      else nextArrow.removeAttribute('disabled');
    }
  }

  function bindMediaPagination(pagination) {
    if (!pagination || pagination.dataset.cosmoshopPaginationReady === 'true') return;
    const slider = pagination.closest('slider-component');
    const mediaList = slider && slider.querySelector('.product__media-list');
    if (!mediaList) return;

    pagination.dataset.cosmoshopPaginationReady = 'true';
    let ticking = false;

    mediaList.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updatePaginationFromScroll(pagination);
        ticking = false;
      });
    }, { passive: true });

    updatePaginationFromScroll(pagination);
  }

  function initMediaPagination(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cosmoshop-media-pagination]').forEach(bindMediaPagination);
  }

  function bindProductMediaWheelNavigation(slider) {
    if (!slider || slider.dataset.cosmoshopMediaWheelReady === 'true') return;

    const mediaList = slider.querySelector('.product__media-list');
    const slides = mediaList
      ? Array.from(mediaList.querySelectorAll('.product__media-item'))
      : [];

    if (!mediaList || slides.length < 2) return;

    slider.dataset.cosmoshopMediaWheelReady = 'true';
    slider.classList.add('cosmoshop-product-media--wheel-navigation');

    let wheelLocked = false;
    let wheelAnimationFrame = null;
    let wheelReleaseTimer = null;
    let snapTarget = null;

    const nearestSlideIndex = (basePosition = mediaList.scrollLeft) => {
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - basePosition);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    };

    const releaseWheel = () => {
      wheelLocked = false;
      snapTarget = null;
    };

    const scheduleWheelRelease = () => {
      window.clearTimeout(wheelReleaseTimer);
      wheelReleaseTimer = window.setTimeout(() => {
        wheelReleaseTimer = null;
        if (!wheelAnimationFrame) releaseWheel();
      }, 120);
    };

    const animateToSlide = (slide) => {
      if (!slide) return;

      if (wheelAnimationFrame) {
        window.cancelAnimationFrame(wheelAnimationFrame);
      }

      const start = mediaList.scrollLeft;
      const target = slide.offsetLeft;
      const distance = target - start;
      const duration = 340;
      const startTime = performance.now();
      const previousScrollBehavior = mediaList.style.scrollBehavior;
      const previousSnapType = mediaList.style.scrollSnapType;

      mediaList.style.setProperty('scroll-behavior', 'auto', 'important');
      mediaList.style.setProperty('scroll-snap-type', 'none', 'important');

      const easeInOut = (progress) => progress * progress * (3 - (2 * progress));

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(1, elapsed / duration);
        mediaList.scrollLeft = start + (distance * easeInOut(progress));

        if (progress < 1) {
          wheelAnimationFrame = window.requestAnimationFrame(animate);
          return;
        }

        mediaList.scrollLeft = target;
        wheelAnimationFrame = null;

        if (previousScrollBehavior) {
          mediaList.style.scrollBehavior = previousScrollBehavior;
        } else {
          mediaList.style.removeProperty('scroll-behavior');
        }

        if (previousSnapType) {
          mediaList.style.scrollSnapType = previousSnapType;
        } else {
          mediaList.style.removeProperty('scroll-snap-type');
        }

        const pagination = slider.querySelector('[data-cosmoshop-media-pagination]');
        if (pagination) updatePaginationFromScroll(pagination);
        updateProductMediaArrowContrast(slider);

        if (!wheelReleaseTimer) releaseWheel();
      };

      wheelAnimationFrame = window.requestAnimationFrame(animate);
    };

    mediaList.addEventListener('wheel', (event) => {
      if (event.ctrlKey) return;

      const dominantDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;

      if (Math.abs(dominantDelta) < 1) return;

      if (wheelLocked) {
        event.preventDefault();
        scheduleWheelRelease();
        return;
      }

      const direction = Math.sign(dominantDelta);
      const currentIndex = nearestSlideIndex();
      const targetIndex = currentIndex + direction;

      // At the first and last image, return the wheel to normal page scrolling.
      if (targetIndex < 0 || targetIndex >= slides.length) return;

      event.preventDefault();
      wheelLocked = true;
      snapTarget = slides[targetIndex].offsetLeft;
      scheduleWheelRelease();
      animateToSlide(slides[targetIndex]);
    }, { passive: false });

    // Keep a manually swiped gallery aligned before the next wheel step.
    mediaList.addEventListener('scroll', () => {
      if (!wheelLocked || snapTarget === null) return;
      scheduleWheelRelease();
    }, { passive: true });
  }

  function initProductMediaWheelNavigation(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.cosmoshop-product-page slider-component').forEach(bindProductMediaWheelNavigation);
  }

  function clampNumber(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function whenProductMediaImageReady(img, callback) {
    if (!img || typeof callback !== 'function') return;
    if (img.complete && img.naturalWidth && img.naturalHeight) {
      callback();
      return;
    }
    img.addEventListener('load', callback, { once: true });
  }

  function getProductMediaCanvas(img) {
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;

    const canvasWidth = 260;
    const canvasHeight = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * canvasWidth));
    const cacheKey = `${canvasWidth}x${canvasHeight}:${img.currentSrc || img.src || ''}`;

    if (img._cosmoshopProductContrastCache && img._cosmoshopProductContrastCache.key === cacheKey) {
      return img._cosmoshopProductContrastCache;
    }

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    try {
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
    } catch (error) {
      return null;
    }

    img._cosmoshopProductContrastCache = { key: cacheKey, canvas, ctx };
    return img._cosmoshopProductContrastCache;
  }

  function getProductMediaAverageLuminance(img, button) {
    const scaled = getProductMediaCanvas(img);
    if (!scaled || !button) return null;

    const imageRect = img.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    if (!imageRect.width || !imageRect.height || !buttonRect.width || !buttonRect.height) return null;

    const relativeX = ((buttonRect.left + (buttonRect.width / 2)) - imageRect.left) / imageRect.width;
    const relativeY = ((buttonRect.top + (buttonRect.height / 2)) - imageRect.top) / imageRect.height;
    const x = clampNumber(Math.round(relativeX * scaled.canvas.width), 0, scaled.canvas.width - 1);
    const y = clampNumber(Math.round(relativeY * scaled.canvas.height), 0, scaled.canvas.height - 1);
    const radius = 12;
    const sx = clampNumber(x - radius, 0, scaled.canvas.width - 1);
    const sy = clampNumber(y - radius, 0, scaled.canvas.height - 1);
    const sw = clampNumber((radius * 2) + 1, 1, scaled.canvas.width - sx);
    const sh = clampNumber((radius * 2) + 1, 1, scaled.canvas.height - sy);

    try {
      const data = scaled.ctx.getImageData(sx, sy, sw, sh).data;
      let r = 0;
      let g = 0;
      let b = 0;
      let count = 0;

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha < 10) continue;
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count += 1;
      }

      if (!count) return null;
      r /= count;
      g /= count;
      b /= count;

      return ((0.2126 * r) + (0.7152 * g) + (0.0722 * b)) / 255;
    } catch (error) {
      return null;
    }
  }

  function backgroundColorLuminance(element) {
    let node = element;

    while (node && node.nodeType === 1) {
      const color = window.getComputedStyle(node).backgroundColor || '';
      const match = color.match(/rgba?\(([^)]+)\)/i);
      if (match) {
        const parts = match[1].split(',').map((part) => Number.parseFloat(part));
        const alpha = parts.length > 3 ? parts[3] : 1;
        if (parts.length >= 3 && alpha > 0.05) {
          return ((0.2126 * parts[0]) + (0.7152 * parts[1]) + (0.0722 * parts[2])) / 255;
        }
      }
      node = node.parentElement;
    }

    return 1;
  }

  function activeProductMediaImage(slider) {
    const mediaList = slider && slider.querySelector('.product__media-list');
    if (!mediaList) return null;

    const slides = Array.from(mediaList.querySelectorAll('.product__media-item'));
    if (!slides.length) return null;

    let activeSlide = slides[0];
    let smallestDistance = Infinity;

    slides.forEach((slide) => {
      const distance = Math.abs(slide.offsetLeft - mediaList.scrollLeft);
      if (distance < smallestDistance) {
        activeSlide = slide;
        smallestDistance = distance;
      }
    });

    return activeSlide.querySelector('.product__media-display img, img');
  }

  function setProductMediaArrowContrast(slider, arrow, colorVar, shadowVar) {
    if (!slider || !arrow) return false;

    const img = activeProductMediaImage(slider);
    const apply = (luminance) => {
      const useWhite = luminance < mediaArrowContrastThreshold;
      slider.style.setProperty(colorVar, useWhite ? '#ffffff' : '#000000');
      slider.style.setProperty(shadowVar, useWhite ? 'rgba(0, 0, 0, 0.55)' : 'rgba(255, 255, 255, 0.75)');
      slider.dataset.cosmoshopMediaArrowContrastReady = 'true';
    };

    if (!img) {
      apply(backgroundColorLuminance(arrow.parentElement || slider));
      return true;
    }

    whenProductMediaImageReady(img, () => {
      const luminance = getProductMediaAverageLuminance(img, arrow);

      if (luminance === null) {
        slider.removeAttribute('data-cosmoshop-media-arrow-contrast-ready');
        return;
      }

      apply(luminance);
    });

    return true;
  }

  function updateProductMediaArrowContrast(slider) {
    if (!slider) return;

    const prev = slider.querySelector('.cosmoshop-media-arrow--prev');
    const next = slider.querySelector('.cosmoshop-media-arrow--next');

    setProductMediaArrowContrast(slider, prev, '--cosmoshop-product-media-prev-arrow-color', '--cosmoshop-product-media-prev-arrow-shadow');
    setProductMediaArrowContrast(slider, next, '--cosmoshop-product-media-next-arrow-color', '--cosmoshop-product-media-next-arrow-shadow');
  }

  function updateAllProductMediaArrowContrast(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.cosmoshop-product-page slider-component').forEach(updateProductMediaArrowContrast);
  }

  function bindProductMediaArrowContrast(slider) {
    if (!slider || slider.dataset.cosmoshopProductMediaArrowReady === 'true') {
      updateProductMediaArrowContrast(slider);
      return;
    }

    const mediaList = slider.querySelector('.product__media-list');
    if (!mediaList || !slider.querySelector('.cosmoshop-media-arrow')) return;

    slider.dataset.cosmoshopProductMediaArrowReady = 'true';
    let ticking = false;

    const scheduleUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateProductMediaArrowContrast(slider);
        ticking = false;
      });
    };

    mediaList.addEventListener('scroll', scheduleUpdate, { passive: true });

    slider.querySelectorAll('.product__media-display img, .product__media-item img').forEach((img) => {
      whenProductMediaImageReady(img, scheduleUpdate);
    });

    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 250);
    window.setTimeout(scheduleUpdate, 900);
  }

  function initProductMediaArrowContrast(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.cosmoshop-product-page slider-component').forEach(bindProductMediaArrowContrast);
  }


  function bindProductImageHoverZoom(display) {
    if (!display || display.dataset.cosmoshopHoverZoomReady === 'true') return;
    display.dataset.cosmoshopHoverZoomReady = 'true';

    const hoverZoomQuery = window.matchMedia ? window.matchMedia('(min-width: 1024px)') : null;

    function clearZoom() {
      display.classList.remove('is-magnifying');
      display.style.removeProperty('--cosmoshop-product-hover-zoom-x');
      display.style.removeProperty('--cosmoshop-product-hover-zoom-y');
    }

    function updateZoom(event) {
      if (hoverZoomQuery && !hoverZoomQuery.matches) {
        clearZoom();
        return;
      }


      const rect = display.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));

      display.style.setProperty('--cosmoshop-product-hover-zoom-x', `${x}%`);
      display.style.setProperty('--cosmoshop-product-hover-zoom-y', `${y}%`);
      display.classList.add('is-magnifying');
    }

    display.addEventListener('pointerenter', updateZoom);
    display.addEventListener('pointermove', updateZoom);
    display.addEventListener('pointerleave', clearZoom);
    display.addEventListener('pointercancel', clearZoom);

    if (hoverZoomQuery && hoverZoomQuery.addEventListener) {
      hoverZoomQuery.addEventListener('change', clearZoom);
    }
  }

  function initProductImageHoverZoom(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.cosmoshop-product-page .product__media-display--image').forEach(bindProductImageHoverZoom);
  }



  function bindSmoothProductAccordion(details) {
    if (!details || details.dataset.cosmoshopAccordionReady === 'true') return;

    const summary = details.querySelector('summary');
    const content = details.querySelector('.accordion__content');
    if (!summary || !content) return;

    details.dataset.cosmoshopAccordionReady = 'true';
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    if (details.open) {
      content.style.maxHeight = 'none';
      content.style.opacity = '1';
    } else {
      content.style.maxHeight = '0px';
      content.style.opacity = '0';
    }

    function finishOpen(event) {
      if (event && event.target !== content) return;
      if (event && event.propertyName !== 'max-height') return;
      if (details.open && !details.classList.contains('is-closing')) {
        content.style.maxHeight = 'none';
      }
      details.classList.remove('is-opening');
      content.removeEventListener('transitionend', finishOpen);
    }

    function finishClose(event) {
      if (event && event.target !== content) return;
      if (event && event.propertyName !== 'max-height') return;
      details.open = false;
      details.classList.remove('is-closing');
      summary.setAttribute('aria-expanded', 'false');
      content.removeEventListener('transitionend', finishClose);
    }

    summary.addEventListener('click', (event) => {
      event.preventDefault();

      if (details.classList.contains('is-opening') || details.classList.contains('is-closing')) return;

      if (!details.open) {
        details.open = true;
        details.classList.add('is-opening');
        summary.setAttribute('aria-expanded', 'true');
        content.style.maxHeight = '0px';
        content.style.opacity = '0';

        window.requestAnimationFrame(() => {
          content.style.maxHeight = `${content.scrollHeight}px`;
          content.style.opacity = '1';
          content.addEventListener('transitionend', finishOpen);
          window.setTimeout(() => finishOpen({ target: content, propertyName: 'max-height' }), 440);
        });
        return;
      }

      details.classList.add('is-closing');
      summary.setAttribute('aria-expanded', 'false');
      content.style.maxHeight = `${content.scrollHeight}px`;
      content.style.opacity = '1';

      window.requestAnimationFrame(() => {
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
        content.addEventListener('transitionend', finishClose);
        window.setTimeout(() => finishClose({ target: content, propertyName: 'max-height' }), 440);
      });
    });
  }

  function initSmoothProductAccordions(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.cosmoshop-product-page .product__accordion').forEach(bindSmoothProductAccordion);
  }

  function refreshCartDrawerContent() {
    const reloadTarget = document.getElementById('cart_reload');
    if (!reloadTarget || !window.fetch || !window.DOMParser) return Promise.resolve();

    return fetch(window.location.href.split('#')[0], { credentials: 'same-origin' })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshContent = doc.querySelector('#cart_reload .cart_content');
        if (freshContent) reloadTarget.innerHTML = freshContent.outerHTML;
      })
      .catch(() => null);
  }

  function initCartDrawerAccess() {
    if (document.documentElement.dataset.cosmoshopCartDrawerReady === 'true') return;
    document.documentElement.dataset.cosmoshopCartDrawerReady = 'true';

    function drawerElements() {
      return {
        drawer: document.querySelector('.cart-content.cosmoshop-cart-drawer, .cart-content'),
        overlay: document.querySelector('.hearder-cart > .cosmoshop-cart-overlay, .hearder-cart > .overlay'),
        loader: document.getElementById('loader')
      };
    }

    function setCartDrawerLoading(isLoading) {
      const { drawer, loader } = drawerElements();
      const active = Boolean(isLoading);

      if (drawer) drawer.classList.toggle('is-loading', active);
      if (loader) {
        loader.classList.toggle('loader', active);
        loader.classList.toggle('is-loading', active);
        loader.setAttribute('aria-hidden', active ? 'false' : 'true');
      }
    }

    function openCartDrawer() {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      if (document.body.classList.contains('template-cart')) {
        window.location.href = cartUrl;
        return;
      }

      const { drawer, overlay, loader } = drawerElements();
      if (!drawer) {
        window.location.href = cartUrl;
        return;
      }

      drawer.classList.add('active');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('overlay_active');
      document.body.classList.add('cosmoshop-cart-open');

      setCartDrawerLoading(true);
      refreshCartDrawerContent().finally(() => {
        setCartDrawerLoading(false);
        window.setTimeout(() => drawer.focus({ preventScroll: true }), 40);
      });
    }

    function closeCartDrawer() {
      const { drawer, overlay, loader } = drawerElements();
      if (drawer) {
        drawer.classList.remove('active');
        drawer.setAttribute('aria-hidden', 'true');
      }
      if (overlay) overlay.classList.remove('overlay_active');
      setCartDrawerLoading(false);
      document.body.classList.remove('cosmoshop-cart-open');
    }

    document.addEventListener('click', (event) => {
      const cartButton = event.target.closest('#cart-icon-bubble');
      if (cartButton) {
        event.preventDefault();
        event.stopPropagation();
        openCartDrawer();
        return;
      }

      if (event.target.closest('.cosmoshop-cart-drawer__close, .close-icon') || event.target.closest('.hearder-cart > .cosmoshop-cart-overlay, .hearder-cart > .overlay')) {
        event.preventDefault();
        closeCartDrawer();
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeCartDrawer();
    });
  }

  function goToMediaDot(dot) {
    const pagination = dot.closest('[data-cosmoshop-media-pagination]');
    const slider = pagination && pagination.closest('slider-component');
    const mediaList = slider && slider.querySelector('.product__media-list');
    if (!pagination || !mediaList) return;

    const index = Number(dot.getAttribute('data-cosmoshop-slide-index') || 0);
    const slides = Array.from(mediaList.querySelectorAll('.product__media-item'));
    const slide = slides[index];
    if (!slide) return;

    setMediaPaginationActive(pagination, index);
    if (typeof mediaList.scrollTo === 'function') {
      mediaList.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    } else {
      mediaList.scrollLeft = slide.offsetLeft;
    }
  }

  function onProductChange(event) {
    const target = event.target;
    if (!target) return;

    const variantPicker = target.closest('variant-selects, variant-radios');
    if (target.name === 'id' || target.name === 'options[]' || target.name === 'quantity' || target.closest('variant-selects, variant-radios, quantity-input')) {
      window.setTimeout(updateAllInstallments, 0);
    }

    if (variantPicker) {
      window.setTimeout(() => {
        const available = variantPicker.currentVariant
          ? variantPicker.currentVariant.available !== false
          : false;
        const scope = productScope(variantPicker);
        setPaymentOptionVisibility(scope, available);
        updateProductDescriptionAvailability(scope, available);
      }, 0);
    }
  }


  function updateGstLabels(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    const rows = Array.from(scope.querySelectorAll('.product-price.price-with-gst'));

    rows.forEach((row) => {
      const label = row.querySelector('.gst-included');
      if (!label) return;

      const fullText = label.getAttribute('data-gst-full') || 'GST inclusive';
      const shortText = label.getAttribute('data-gst-short') || 'GST incl.';
      const host = row.closest('.price-product') || row.parentElement;
      const availableWidth = host ? host.clientWidth : row.clientWidth;

      label.textContent = fullText;
      row.classList.remove('cosmoshop-gst-label-compact');

      window.requestAnimationFrame(() => {
        const fullWidth = row.scrollWidth;
        const currentAvailableWidth = host ? host.clientWidth : availableWidth;
        if (currentAvailableWidth && fullWidth > currentAvailableWidth + 1) {
          label.textContent = shortText;
          row.classList.add('cosmoshop-gst-label-compact');
        }
      });
    });
  }

  const cosmoshopSpecLabels = [
    'model',
    'model number',
    'sku',
    'style',
    'style highlights',
    'movement',
    'movement type',
    'calibre',
    'caliber',
    'case size',
    'case diameter',
    'case width',
    'case length',
    'case thickness',
    'case shape',
    'case material',
    'case colour',
    'case color',
    'dial',
    'dial colour',
    'dial color',
    'bezel',
    'bezel material',
    'bezel colour',
    'bezel color',
    'crystal',
    'crystal material',
    'glass type',
    'glass material',
    'strap',
    'strap material',
    'band type',
    'band material',
    'band colour',
    'band color',
    'band width',
    'band length',
    'bracelet',
    'clasp',
    'clasp type',
    'water resistance',
    'functions',
    'features',
    'power reserve',
    'battery life',
    'warranty',
    'gender',
    'age group',
    'category',
    'collection',
    'lug width',
    'weight',
    'lens material',
    'lens colour',
    'lens color',
    'lens width',
    'bridge width',
    'temple length',
    'frame material',
    'frame colour',
    'frame color',
    'uv protection',
    'polarised',
    'polarized',
    'material',
    'metal',
    'stone',
    'chain length',
    'dimensions'
  ];

  function cleanSpecText(value) {
    return String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^[\s:：\-–—]+|[\s:：\-–—]+$/g, '')
      .trim();
  }

  function isSpecificationLabel(label) {
    const normal = cleanSpecText(label).toLowerCase();
    return cosmoshopSpecLabels.includes(normal);
  }

  function titleCaseSpecLabel(label) {
    const clean = cleanSpecText(label);
    const overrides = {
      sku: 'SKU',
      uv: 'UV',
      gps: 'GPS',
      nfc: 'NFC',
      wr: 'WR'
    };

    return clean.split(/\s+/).map((word) => {
      const key = word.toLowerCase();
      if (overrides[key]) return overrides[key];
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }

  function parseSpecificationRows(source, allowAnyLabel = false) {
    if (!source) return [];

    const lines = (source.innerText || source.textContent || '')
      .split(/[\r\n]+/)
      .map(cleanSpecText)
      .filter(Boolean);

    if (!lines.length) return [];

    const specificationHeadingIndex = lines.findIndex((line) => /^specifications?\s*:?$/i.test(line));
    const rows = [];
    const seen = new Set();

    lines.forEach((line, index) => {
      if (specificationHeadingIndex !== -1 && index <= specificationHeadingIndex) return;
      if (/^specifications?\s*:?$/i.test(line)) return;

      const match = line.match(/^([^:：]{2,72})\s*[:：]\s*(.+)$/);
      if (!match) return;

      const label = cleanSpecText(match[1]);
      const value = cleanSpecText(match[2]);
      if (!label || !value) return;
      if (!allowAnyLabel && !isSpecificationLabel(label)) return;

      const key = `${label.toLowerCase()}::${value.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({ label: titleCaseSpecLabel(label), value });
    });

    return rows;
  }

  function findStructuredSpecificationSection(description) {
    if (!description) return null;
    return description.querySelector(
      '[data-cosmoshop-section="specifications"], [data-cosmoshop-product-specifications]'
    );
  }

  function findLegacySpecificationSection(description) {
    if (!description) return null;

    const heading = Array.from(description.querySelectorAll('strong, b, h2, h3, h4, h5'))
      .find((node) => /^specifications?\s*:?$/i.test(cleanSpecText(node.textContent)));

    if (!heading) return null;

    const container = heading.closest('section, div, p');
    if (container && container !== description) return container;
    return heading.parentElement && heading.parentElement !== description ? heading.parentElement : null;
  }

  function removeSpecificationSection(section) {
    if (!section || !section.parentNode) return;

    const previous = section.previousElementSibling;
    const next = section.nextElementSibling;

    // Product descriptions commonly place an <hr> immediately before and after
    // Specifications. Remove only the leading divider, leaving the trailing one
    // to separate What's Included from the business information.
    if (previous && previous.tagName === 'HR') previous.remove();
    else if (!previous && next && next.tagName === 'HR') next.remove();

    section.remove();
  }

  function removeLegacyAvailabilityCopy(description) {
    if (!description || typeof document.createTreeWalker !== 'function') return;

    const patterns = [
      /Pre-order stock is subject to supplier availability\.\s*If the item becomes unavailable after purchase, we will notify you by email as soon as possible and provide a full refund\.?/gi
    ];

    const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      let value = node.nodeValue || '';
      patterns.forEach((pattern) => {
        value = value.replace(pattern, '');
      });
      node.nodeValue = value;
    });

    description.querySelectorAll('p, div').forEach((node) => {
      if (!cleanSpecText(node.textContent) && !node.querySelector('img, video, iframe, a, ul, ol')) {
        node.remove();
      }
    });
  }

  function placeProductAvailabilityNotice(tabs, description) {
    const notice = tabs.querySelector('[data-cosmoshop-product-availability-notice]');
    if (!notice || !description) return;

    const placeholder = description.querySelector(
      '[data-cosmoshop-section="availability"], [data-cosmoshop-product-availability]'
    );

    if (placeholder && placeholder !== notice && !placeholder.contains(notice)) {
      placeholder.replaceWith(notice);
    }
  }

  function placeProductPreOrderNotice(tabs, description) {
    const notice = tabs.querySelector('[data-cosmoshop-product-pre-order-notice]');
    if (!notice || !description) return;

    // Avoid duplicating the notice if a product description already contains it.
    const existingNoticeHeading = Array.from(description.querySelectorAll('strong, b, h2, h3, h4, h5'))
      .find((node) => /^pre-order notice\s*:?$/i.test(cleanSpecText(node.textContent)));
    if (existingNoticeHeading && !notice.contains(existingNoticeHeading)) {
      notice.remove();
      return;
    }

    const placeholder = description.querySelector(
      '[data-cosmoshop-section="pre-order-notice"], [data-cosmoshop-product-pre-order-placeholder]'
    );
    if (placeholder && placeholder !== notice && !placeholder.contains(notice)) {
      placeholder.replaceWith(notice);
      notice.hidden = false;
      return;
    }

    const children = Array.from(description.children);
    const disclaimer = children.find((node) => {
      if (node.matches('[data-cosmoshop-section="disclaimer"], [data-cosmoshop-product-disclaimer]')) return true;
      const text = cleanSpecText(node.textContent);
      return /Cosmoshop NZ is owned and operated by/i.test(text)
        || /Cosmoshop \(Tapui\) Limited/i.test(text);
    });

    if (disclaimer) {
      // In the standard description layout, the retained divider sits immediately
      // before the business disclaimer, so inserting here gives the order:
      // What's Included -> divider -> Pre-Order Notice -> disclaimer.
      description.insertBefore(notice, disclaimer);
    } else {
      // Fallback for older product descriptions without the standard business disclaimer.
      const dividers = children.filter((node) => node.tagName === 'HR');
      const lastDivider = dividers[dividers.length - 1];
      if (lastDivider && lastDivider.nextSibling) {
        description.insertBefore(notice, lastDivider.nextSibling);
      } else {
        description.appendChild(notice);
      }
    }

    notice.hidden = false;
  }

  function removeThemeManagedDescriptionCopy(description) {
    if (!description) return;

    const managedPhrases = [
      /Cosmoshop NZ is owned and operated by Cosmoshop \(Tapui\) Limited/i,
      /^Pre-Order Notice\s*:/i
    ];

    managedPhrases.forEach((pattern) => {
      const paragraphs = Array.from(description.querySelectorAll('p'));
      const paragraph = paragraphs.find((node) => pattern.test(cleanSpecText(node.textContent)));
      if (paragraph) {
        const previous = paragraph.previousElementSibling;
        paragraph.remove();
        if (previous && previous.tagName === 'HR') previous.remove();
        return;
      }

      const blocks = Array.from(description.querySelectorAll('section, div'));
      const block = blocks.find((node) => {
        const text = cleanSpecText(node.textContent);
        return text.length <= 1600 && pattern.test(text);
      });
      if (block) {
        const previous = block.previousElementSibling;
        block.remove();
        if (previous && previous.tagName === 'HR') previous.remove();
      }
    });

    // The theme renders its own divider before the managed notices, so remove
    // legacy trailing dividers left behind in old product descriptions.
    while (description.lastElementChild && description.lastElementChild.tagName === 'HR') {
      description.lastElementChild.remove();
    }
  }

  function setProductAvailabilityNotice(notice, availableOverride) {
    if (!notice) return;

    const isPreOrder = notice.getAttribute('data-cosmoshop-pre-order') === 'true';
    const available = typeof availableOverride === 'boolean'
      ? availableOverride
      : notice.getAttribute('data-cosmoshop-available') === 'true';

    notice.setAttribute('data-cosmoshop-available', available ? 'true' : 'false');
    notice.classList.remove(
      'cosmoshop-product-description-availability--pre-order',
      'cosmoshop-product-description-availability--in-stock',
      'cosmoshop-product-description-availability--out-of-stock'
    );

    const label = notice.querySelector('[data-cosmoshop-availability-label]');
    const message = notice.querySelector('[data-cosmoshop-availability-message]');

    if (isPreOrder) {
      notice.classList.add('cosmoshop-product-description-availability--pre-order');
      if (label) label.textContent = 'Pre-Order :';
      if (message) message.textContent = 'Item dispatches within 5 business days.';
      return;
    }

    if (available) {
      notice.classList.add('cosmoshop-product-description-availability--in-stock');
      if (label) label.textContent = 'In Stock.';
      if (message) message.textContent = 'Dispatch within 1 business day';
      return;
    }

    notice.classList.add('cosmoshop-product-description-availability--out-of-stock');
    if (label) label.textContent = 'Out of Stock';
    if (message) message.textContent = '';
  }

  function updateProductDescriptionAvailability(root = document, availableOverride) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cosmoshop-product-availability-notice]').forEach((notice) => {
      setProductAvailabilityNotice(notice, availableOverride);
    });
  }

  function prepareProductDescription(tabs) {
    const description = tabs.querySelector('[data-cosmoshop-product-description-source]');
    if (!description || tabs.dataset.cosmoshopDescriptionPrepared === 'true') return;

    tabs.dataset.cosmoshopDescriptionPrepared = 'true';
    placeProductAvailabilityNotice(tabs, description);
    removeLegacyAvailabilityCopy(description);
    removeThemeManagedDescriptionCopy(description);

    const structuredSection = findStructuredSpecificationSection(description);
    const legacySection = structuredSection ? null : findLegacySpecificationSection(description);
    const specificationSection = structuredSection || legacySection;

    let rows = [];
    if (specificationSection) {
      rows = parseSpecificationRows(specificationSection, Boolean(structuredSection));
      removeSpecificationSection(specificationSection);
    } else {
      // Backward-compatible fallback for older descriptions that contain plain
      // specification lines but do not wrap them in a dedicated section.
      rows = parseSpecificationRows(description, false);
    }

    tabs._cosmoshopSpecificationRows = rows;
  }

  function renderProductSpecificationTable(tabs) {
    const host = tabs.querySelector('[data-cosmoshop-product-specification]');
    if (!host || host.dataset.cosmoshopSpecsRendered === 'true') return;

    prepareProductDescription(tabs);
    host.dataset.cosmoshopSpecsRendered = 'true';

    const rows = Array.isArray(tabs._cosmoshopSpecificationRows)
      ? tabs._cosmoshopSpecificationRows
      : [];
    if (!rows.length) return;

    const list = document.createElement('dl');
    list.className = 'cosmoshop-product-specification__list';

    rows.forEach((row) => {
      const term = document.createElement('dt');
      term.className = 'cosmoshop-product-specification__term';
      term.textContent = row.label;

      const value = document.createElement('dd');
      value.className = 'cosmoshop-product-specification__value';
      value.textContent = row.value;

      list.append(term, value);
    });

    host.replaceChildren(list);
  }

  function bindProductInfoTabs(tabs) {
    if (!tabs || tabs.dataset.cosmoshopInfoTabsReady === 'true') return;
    tabs.dataset.cosmoshopInfoTabsReady = 'true';

    const buttons = Array.from(tabs.querySelectorAll('[data-cosmoshop-product-tab]'));
    const panels = Array.from(tabs.querySelectorAll('[data-cosmoshop-product-panel]'));

    function activateTab(name) {
      buttons.forEach((button) => {
        const active = button.getAttribute('data-cosmoshop-product-tab') === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        const active = panel.getAttribute('data-cosmoshop-product-panel') === name;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      if (name === 'specification') renderProductSpecificationTable(tabs);
    }

    buttons.forEach((button, index) => {
      button.tabIndex = button.classList.contains('is-active') ? 0 : -1;

      button.addEventListener('click', () => {
        activateTab(button.getAttribute('data-cosmoshop-product-tab'));
      });

      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = index <= 0 ? buttons.length - 1 : index - 1;
        if (event.key === 'ArrowRight') nextIndex = index >= buttons.length - 1 ? 0 : index + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;

        const nextButton = buttons[nextIndex];
        if (nextButton) {
          nextButton.focus();
          activateTab(nextButton.getAttribute('data-cosmoshop-product-tab'));
        }
      });
    });

    renderProductSpecificationTable(tabs);
  }

  function initProductInfoTabs(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cosmoshop-product-info-tabs]').forEach(bindProductInfoTabs);
  }

  function initProductPageRefresh(event) {
    updateAllInstallments();
    updateGstLabels(event && event.target ? event.target : document);
    initMediaPagination(event && event.target ? event.target : document);
    initProductMediaWheelNavigation(event && event.target ? event.target : document);
    initProductMediaArrowContrast(event && event.target ? event.target : document);
    initProductImageHoverZoom(event && event.target ? event.target : document);
    initSmoothProductAccordions(event && event.target ? event.target : document);
    initProductInfoTabs(event && event.target ? event.target : document);
    updateProductDescriptionAvailability(event && event.target ? event.target : document);
    initCartDrawerAccess();
  }

  document.addEventListener('change', onProductChange);
  document.addEventListener('input', onProductChange);
  window.addEventListener('resize', () => window.requestAnimationFrame(() => {
    updateGstLabels();
    updateAllProductMediaArrowContrast();
  }));
  document.addEventListener('variant:change', () => window.setTimeout(() => { updateAllInstallments(); updateGstLabels(); updateProductDescriptionAvailability(); }, 0));
  document.addEventListener('click', (event) => {
    const mediaDot = event.target.closest('[data-cosmoshop-media-dot]');
    if (mediaDot) {
      event.preventDefault();
      goToMediaDot(mediaDot);
      window.setTimeout(() => updateAllProductMediaArrowContrast(mediaDot.closest('.cosmoshop-product-page') || document), 120);
      return;
    }

    const checkoutLink = event.target.closest('[data-cosmoshop-installment-checkout]');
    if (checkoutLink) {
      event.preventDefault();
      beginInstallmentCheckout(checkoutLink);
      return;
    }

    if (event.target.closest('quantity-input .quantity__button')) {
      window.setTimeout(updateAllInstallments, 0);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductPageRefresh, { once: true });
  } else {
    initProductPageRefresh();
  }

  document.addEventListener('shopify:section:load', initProductPageRefresh);
})();


/* Request more pictures inline dropdown + AJAX contact submission. */
(() => {
  'use strict';

  function showSubmitted(form) {
    const root = form.closest('[data-cosmoshop-request-pictures]');
    if (!root) return;

    const wrap = root.querySelector('[data-cosmoshop-request-pictures-form-wrap]');
    const success = root.querySelector('[data-cosmoshop-request-pictures-success]');
    const content = root.querySelector('.accordion__content');

    if (wrap) wrap.hidden = true;
    if (success) success.hidden = false;

    if (content) {
      content.style.maxHeight = content.scrollHeight + 'px';
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.cosmoshop-request-pictures__form');
    if (!form) return;

    event.preventDefault();

    if (!form.reportValidity()) return;

    const submit = form.querySelector('[type="submit"]');
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData(form);
    const action = form.getAttribute('action') || '/contact';

    fetch(action, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'text/html'
      },
      credentials: 'same-origin'
    })
      .then(() => {
        form.reset();
        showSubmitted(form);
      })
      .catch(() => {
        form.reset();
        showSubmitted(form);
      })
      .finally(() => {
        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
      });
  });
})();
